import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Offset, Rect } from '../game/geom';
import { drawBuild, keep, neededImages, type DrawnItem, type Images } from '../game/render';
import { loadImage, type ThemeVisual } from '../game/visual';

interface Props {
  visual: ThemeVisual | null;
  /// Theme item indices the player won, in order.
  won: number[];
  /// Names of the won items (text fallback for themes without artwork).
  names: string[];
  moved: Map<number, Offset>;
  onMove?: (itemIndex: number, centre: Offset) => void;
  popIn?: number | null; // theme item index that scales in
  accent: string;
  className?: string;
}

/// A player's build: canvas for illustrated themes (draggable stickers),
/// a numbered list for word-list themes.
export function BuildCanvas({ visual, won, names, moved, onMove, popIn, accent, className }: Props) {
  if (!visual) return <BuildList names={names} accent={accent} className={className} />;
  return (
    <BuildArt visual={visual} won={won} moved={moved} onMove={onMove} popIn={popIn ?? null} className={className} />
  );
}

function BuildList({ names, accent, className }: { names: string[]; accent: string; className?: string }) {
  const rows = Math.max(5, names.length);
  return (
    <ol className={`build-list ${className ?? ''}`} style={{ ['--rows' as string]: rows }}>
      {Array.from({ length: rows }, (_, i) => (
        <li key={i}>
          <span className="n" style={{ color: accent }}>
            {i + 1}.
          </span>
          <span className="t">{names[i] ?? ''}</span>
        </li>
      ))}
    </ol>
  );
}

function BuildArt({
  visual,
  won,
  moved,
  onMove,
  popIn,
  className,
}: {
  visual: ThemeVisual;
  won: number[];
  moved: Map<number, Offset>;
  onMove?: (itemIndex: number, centre: Offset) => void;
  popIn: number | null;
  className?: string;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [px, setPx] = useState(0);
  const [imgs, setImgs] = useState<Images>(new Map());
  const drawn = useRef<{ items: DrawnItem[]; frameSide: number }>({ items: [], frameSide: 1 });
  const popStart = useRef<{ itemIndex: number; t0: number } | null>(null);
  const drag = useRef<{ item: DrawnItem; grab: Offset; pointer: number } | null>(null);

  // Canvas resolution follows its displayed size (and the stage's zoom).
  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setPx(Math.max(1, Math.round(Math.min(r.width, r.height) * Math.min(2, window.devicePixelRatio || 1))));
    };
    update();
    // The stage scales with a CSS transform, which ResizeObserver doesn't see.
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  // Load what this build needs; draw with what is there meanwhile.
  const urls = neededImages({ visual, won, moved });
  const urlKey = urls.join('|');
  useEffect(() => {
    let alive = true;
    Promise.all(urls.map((u) => loadImage(u).then((i) => [u, i] as const).catch(() => null))).then((list) => {
      if (!alive) return;
      const m = new Map<string, HTMLImageElement>();
      for (const e of list) if (e) m.set(e[0], e[1]);
      setImgs(m);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey]);

  useEffect(() => {
    if (popIn !== null && popIn !== undefined) popStart.current = { itemIndex: popIn, t0: performance.now() };
  }, [popIn, won.length]);

  useEffect(() => {
    const c = canvas.current;
    if (!c || px < 2) return;
    if (c.width !== px) {
      c.width = px;
      c.height = px;
    }
    const ctx = c.getContext('2d');
    if (!ctx) return;
    let raf = 0;
    const frame = () => {
      const p = popStart.current;
      const t = p ? (performance.now() - p.t0) / 250 : 1;
      drawn.current = drawBuild(ctx, px, {
        visual,
        won,
        moved,
        popIn: p && t < 1 ? { itemIndex: p.itemIndex, t } : null,
      }, imgs);
      if (p && t < 1) raf = requestAnimationFrame(frame);
      else popStart.current = null;
    };
    frame();
    return () => cancelAnimationFrame(raf);
  }, [px, visual, won, moved, imgs]);

  // ─── Dragging ───
  const toFrame = (e: React.PointerEvent): Offset => {
    const r = canvas.current!.getBoundingClientRect();
    const k = drawn.current.frameSide / r.width;
    return new Offset((e.clientX - r.left) * k, (e.clientY - r.top) * k);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!onMove) return;
    const p = toFrame(e);
    // Topmost first: the drawn list is in paint order.
    const hit = [...drawn.current.items].reverse().find((it) => it.at.contains(p));
    if (!hit) return;
    e.preventDefault();
    (e.target as Element).setPointerCapture(e.pointerId);
    drag.current = { item: hit, grab: p.minus(hit.at.center), pointer: e.pointerId };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.pointer !== e.pointerId || !onMove) return;
    const p = toFrame(e);
    const want = p.minus(d.grab);
    // Keep it inside the picture, like the app's _DragItem.
    const planned: Rect = d.item.planned;
    const shift = keep(planned, want.minus(planned.center), d.item.bounds);
    onMove(d.item.itemIndex, planned.center.plus(shift).plus(d.item.origin));
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (drag.current?.pointer === e.pointerId) drag.current = null;
  };

  return (
    <div ref={wrap} className={`build-art ${className ?? ''}`} style={{ background: visual.backdrop }}>
      <canvas
        ref={canvas}
        className={onMove ? 'draggable' : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
    </div>
  );
}
